import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/ui/signed-avatar-image';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Search, FileText } from 'lucide-react';
import { useServerPagination } from '@/hooks/useServerPagination';
import { ListPagination } from '@/components/ListPagination';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { useTableSort } from '@/hooks/useTableSort';
import { getSbtPhotoUrl, getColorValue } from '@/lib/vehiclePhoto';
import { format } from 'date-fns';

interface ContractsRecordsProps {
    monthRange?: { from: Date; to: Date };
}

const ITEMS_PER_PAGE = 15;

export default function ContractsRecords({ monthRange }: ContractsRecordsProps) {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [statusFilter, setStatusFilter] = useState<string>('all');

    const fromDate = monthRange ? format(monthRange.from, 'yyyy-MM-dd') : null;
    const toDate = monthRange ? format(monthRange.to, 'yyyy-MM-dd') : null;
    const { sortKey, sortDirection, handleSort } = useTableSort({
        defaultSortKey: 'signed_at',
        defaultSortDirection: 'desc',
    });

    // Track total count for pagination (initially 0, updated by RPC)
    const [totalCount, setTotalCount] = useState(0);

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } = useServerPagination({
        totalCount: totalCount,
        itemsPerPage: ITEMS_PER_PAGE,
    });

    // Fetch contracts with server-side status filtering
    const { data: rpcResult, isLoading } = useQuery({
        queryKey: ['contracts', currentPage, sortKey, sortDirection, statusFilter, debouncedSearch, fromDate, toDate],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_contracts_with_status', {
                status_filter: statusFilter === 'all' ? null : statusFilter,
                search_query: debouncedSearch || null,
                page_from: from,
                page_to: to,
                sort_column: sortKey,
                sort_ascending: sortDirection === 'asc',
                p_from_date: fromDate,
                p_to_date: toDate,
            });
            if (error) throw error;
            return data as { data: any[]; count: number };
        },
    });

    const contracts = rpcResult?.data || [];

    // Partner names for the vehicles on this page (the RPC payload carries no partner)
    const vehicleIds: number[] = contracts
        .map((c: { vehicles?: { id?: number } }) => c.vehicles?.id)
        .filter((id: number | undefined): id is number => typeof id === 'number');

    const { data: vehiclePartners } = useQuery({
        queryKey: ['contract-vehicle-partners', vehicleIds],
        enabled: vehicleIds.length > 0,
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicles')
                .select('id, partners(name)')
                .in('id', vehicleIds);
            if (error) throw error;
            const map = new Map<number, string>();
            for (const row of data || []) {
                const partner = Array.isArray(row.partners) ? row.partners[0] : row.partners;
                if (partner?.name) map.set(row.id, partner.name);
            }
            return map;
        },
    });

    // Update total count when RPC returns
    useEffect(() => {
        if (rpcResult?.count !== undefined) {
            setTotalCount(rpcResult.count);
        }
    }, [rpcResult?.count]);

    useEffect(() => {
        resetPage();
    }, [debouncedSearch, statusFilter, fromDate, toDate]);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
    };

    const getLatestStatus = (contract: any) => {
        const updates = contract.contract_updates || [];
        if (updates.length === 0) return null;
        const latest = updates.sort((a: any, b: any) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )[0];
        return latest?.status || null;
    };

    const getDriverName = (contract: any) => {
        const profile = Array.isArray(contract.drivers?.profiles)
            ? contract.drivers.profiles[0]
            : contract.drivers?.profiles;
        if (!profile) return 'N/A';
        return [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(' ') || 'N/A';
    };

    const getDriverPhone = (contract: any) => {
        const profile = Array.isArray(contract.drivers?.profiles)
            ? contract.drivers.profiles[0]
            : contract.drivers?.profiles;
        return profile?.phone || '';
    };

    const getDriverPhoto = (contract: any) => {
        const profile = Array.isArray(contract.drivers?.profiles)
            ? contract.drivers.profiles[0]
            : contract.drivers?.profiles;
        return profile?.photo || null;
    };

    const getDriverInitials = (contract: any) => {
        const profile = Array.isArray(contract.drivers?.profiles)
            ? contract.drivers.profiles[0]
            : contract.drivers?.profiles;
        if (!profile) return '?';
        return `${(profile.first_name || '')[0] || ''}${(profile.last_name || '')[0] || ''}`;
    };

    const getStateBadge = (status: string | null) => {
        switch (status) {
            case 'DRAFT':
                return { label: 'Draft', className: 'border-amber-200 bg-amber-50 text-amber-700' };
            case 'IN_PROGRESS':
                return { label: 'Active', className: 'border-green-200 bg-green-50 text-green-700' };
            case 'COMPLETED':
                return { label: 'Completed', className: 'border-blue-200 bg-blue-50 text-blue-700' };
            case 'TERMINATED':
                return { label: 'Terminated', className: 'border-red-200 bg-red-50 text-red-700' };
            default:
                return { label: 'Draft', className: 'border-amber-200 bg-amber-50 text-amber-700' };
        }
    };

    // Server-side filtering is already applied via RPC
    const displayContracts = contracts ?? [];

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-4">
                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search driver, vehicle, contract..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="DRAFT">Draft</SelectItem>
                            <SelectItem value="IN_PROGRESS">Active</SelectItem>
                            <SelectItem value="COMPLETED">Completed</SelectItem>
                            <SelectItem value="TERMINATED">Terminated</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {!isLoading && (
                    <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onPageChange={goToPage}
                    />
                )}
            </div>
            {isLoading ? (
                <div className="space-y-3">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <SortableTableHead sortKey="id" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort}>
                                        Contract
                                    </SortableTableHead>
                                    <SortableTableHead>Driver</SortableTableHead>
                                    <SortableTableHead>Vehicle</SortableTableHead>
                                    <SortableTableHead sortKey="signed_at" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort}>
                                        Signed
                                    </SortableTableHead>
                                    <SortableTableHead sortKey="sold_at" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort}>
                                        Sold At
                                    </SortableTableHead>
                                    <SortableTableHead>Status</SortableTableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {displayContracts.map((contract) => {
                                    const status = getLatestStatus(contract);
                                    const badge = getStateBadge(status);
                                    const vehicleReg = contract.vehicles?.vehicle_registrations;
                                    const regNumber = Array.isArray(vehicleReg) ? vehicleReg[0]?.number : vehicleReg?.number;
                                    return (
                                        <TableRow key={contract.id}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary">
                                                        <FileText className="h-4 w-4 text-secondary-foreground" />
                                                    </div>
                                                    <button
                                                        onClick={() => navigate(`/dashboard/contracts/${contract.id}`)}
                                                        className="font-medium text-primary hover:underline"
                                                    >
                                                        #{contract.id}
                                                    </button>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Avatar className="h-8 w-8">
                                                        <SignedAvatarImage src={getDriverPhoto(contract) || undefined} alt={getDriverName(contract)} />
                                                        <AvatarFallback className="text-[10px] bg-muted">{getDriverInitials(contract)}</AvatarFallback>
                                                    </Avatar>
                                                    <div>
                                                        <p className="font-medium">{getDriverName(contract)}</p>
                                                        <p className="text-xs text-muted-foreground">{getDriverPhone(contract)}</p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {contract.vehicles ? (
                                                    <div className="flex items-center gap-2">
                                                        {(() => {
                                                            const listingUrl = contract.vehicles.vehicle_procurement?.listing_url || null;
                                                            const photoUrl = getSbtPhotoUrl(listingUrl, contract.vehicles.updated_at);
                                                            if (photoUrl) {
                                                                return (
                                                                    <HoverCard openDelay={200} closeDelay={100}>
                                                                        <HoverCardTrigger asChild>
                                                                            <img
                                                                                src={photoUrl}
                                                                                alt=""
                                                                                className="h-8 w-8 rounded-md border border-gray-200 object-cover shrink-0 cursor-zoom-in"
                                                                                onError={(e) => {
                                                                                    e.currentTarget.style.display = 'none';
                                                                                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                                                                }}
                                                                            />
                                                                        </HoverCardTrigger>
                                                                        <HoverCardContent side="right" align="center" className="w-auto p-1" sideOffset={8}>
                                                                            <img src={photoUrl} alt={`${contract.vehicles.make} ${contract.vehicles.model}`} className="w-[480px] rounded-md" />
                                                                        </HoverCardContent>
                                                                    </HoverCard>
                                                                );
                                                            }
                                                            return null;
                                                        })()}
                                                        <div
                                                            className={`h-8 w-8 rounded-md border border-gray-200 shrink-0${
                                                                getSbtPhotoUrl(contract.vehicles.vehicle_procurement?.listing_url || null, contract.vehicles.updated_at) ? ' hidden' : ''
                                                            }`}
                                                            style={{ backgroundColor: getColorValue(contract.vehicles.color || '') || '#e5e7eb' }}
                                                        />
                                                        <div>
                                                            <span className="font-medium">{regNumber || contract.vehicles.vin || 'Unregistered'}</span>
                                                            <p className="text-xs text-muted-foreground">{vehiclePartners?.get(contract.vehicles.id) || 'No partner'}</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground">N/A</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {contract.signed_at && new Date(contract.signed_at).getFullYear() > 1970
                                                    ? new Date(contract.signed_at).toLocaleDateString()
                                                    : '-'}
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {contract.sold_at ? formatCurrency(contract.sold_at) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={badge.className}>{badge.label}</Badge>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {displayContracts.length === 0 && !isLoading && (
                                    <TableRow>
                                        <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                                            {search || statusFilter !== 'all' ? 'No contracts match your filters' : 'No contracts found'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </>
            )}
        </div>
    );
}
